/**
 * Security Monitor Service
 * FREE - Monitors security events and alerts
 */

import { AuditLogger } from './auditLogger';
import { collection, addDoc, query, where, orderBy, limit, getDocs, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface SecurityAlert {
  id: string;
  type: 'LOGIN_FAILURE' | 'SUSPICIOUS_ACTIVITY' | 'RATE_LIMIT_EXCEEDED' | 'UNAUTHORIZED_ACCESS';
  message: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  timestamp: Date;
  userId?: string;
  userEmail?: string;
  details?: any;
}

export class SecurityMonitor {
  private static alerts: SecurityAlert[] = [];
  private static loginAttempts: Map<string, { count: number; lastAttempt: Date }> = new Map();
  private static readonly MAX_LOGIN_ATTEMPTS = 5;
  private static readonly LOGIN_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
  private static readonly ALERT_RETENTION_MS = 24 * 60 * 60 * 1000; // 24 hours
  private static readonly STORAGE_KEY = 'security_monitor_data';
  
  // Firestore collections for centralized monitoring
  private static readonly LOGIN_ATTEMPTS_COLLECTION = 'loginAttempts';
  private static readonly SECURITY_ALERTS_COLLECTION = 'securityAlerts';

  /**
   * Initialize security monitor (load from storage)
   */
  static initialize(): void {
    try {
      console.log('🔒 Initializing Security Monitor...');
      const stored = localStorage.getItem(this.STORAGE_KEY);
      console.log('🔒 Stored data from localStorage:', stored);
      
      if (stored) {
        const data = JSON.parse(stored);
        console.log('🔒 Parsed data:', data);
        this.alerts = data.alerts || [];
        this.loginAttempts = new Map(data.loginAttempts || []);
        console.log('🔒 Security Monitor initialized with stored data');
        console.log('🔒 Loaded alerts:', this.alerts.length);
        console.log('🔒 Loaded login attempts:', this.loginAttempts.size);
      } else {
        console.log('🔒 No stored data found, starting fresh');
      }
    } catch (error) {
      console.error('Error initializing Security Monitor:', error);
    }
  }

  /**
   * Save security monitor data to storage
   */
  private static saveToStorage(): void {
    try {
      const data = {
        alerts: this.alerts,
        loginAttempts: Array.from(this.loginAttempts.entries())
      };
      console.log('🔒 Saving to localStorage:', data);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      console.log('🔒 Data saved successfully');
    } catch (error) {
      console.error('Error saving Security Monitor data:', error);
    }
  }

  /**
   * Monitor login attempt
   */
  static async monitorLoginAttempt(
    userEmail: string,
    success: boolean,
    userId?: string
  ): Promise<void> {
    const now = new Date();
    const key = userEmail.toLowerCase();

    // Store login attempt in Firestore for centralized monitoring
    try {
      const ipAddress = await this.getClientIP();
      const loginAttemptData = {
        userEmail: userEmail.toLowerCase(),
        success,
        userId: userId || null,
        timestamp: serverTimestamp(),
        ipAddress: ipAddress,
        userAgent: navigator.userAgent
      };
      
      console.log('🔒 Attempting to store login attempt in Firestore:', loginAttemptData);
      
      const docRef = await addDoc(collection(db, this.LOGIN_ATTEMPTS_COLLECTION), loginAttemptData);
      
      console.log(`🔒 ✅ Login attempt stored in Firestore: ${userEmail} (success: ${success}) - Document ID: ${docRef.id}`);
    } catch (error) {
      console.error('🔒 ❌ Failed to store login attempt in Firestore:', error);
      console.error('🔒 Error details:', JSON.stringify(error, null, 2));
    }

    if (!success) {
      // Track failed attempts
      const attempts = this.loginAttempts.get(key) || { count: 0, lastAttempt: now };
      attempts.count++;
      attempts.lastAttempt = now;
      this.loginAttempts.set(key, attempts);
      
      console.log(`🔒 Login failure tracked: ${userEmail} (attempt ${attempts.count})`);
      this.saveToStorage(); // Save to localStorage

      // Check for suspicious activity
      if (attempts.count >= this.MAX_LOGIN_ATTEMPTS) {
        await this.createAlert(
          'LOGIN_FAILURE',
          `Multiple failed login attempts for ${userEmail}`,
          'HIGH',
          userId,
          userEmail,
          { attempts: attempts.count, timeWindow: '15 minutes' }
        );
      }
    } else {
      // Reset attempts on successful login
      this.loginAttempts.delete(key);
      console.log(`🔒 Login success: ${userEmail} - attempts reset`);
      this.saveToStorage(); // Save to localStorage
    }

    // Log the attempt
    await AuditLogger.logLoginAttempt(userEmail, success);
  }

  /**
   * Monitor invitation code usage
   */
  static async monitorInvitationCodeUsage(
    userId: string,
    userEmail: string,
    invitationCode: string,
    success: boolean
  ): Promise<void> {
    if (!success) {
      await this.createAlert(
        'SUSPICIOUS_ACTIVITY',
        `Invalid invitation code usage attempt by ${userEmail}`,
        'MEDIUM',
        userId,
        userEmail,
        { invitationCode }
      );
    }

    await AuditLogger.logInvitationCodeUsage(userId, userEmail, invitationCode, success);
  }

  /**
   * Monitor role changes
   */
  static async monitorRoleChange(
    userId: string,
    userEmail: string,
    targetUserId: string,
    targetUserEmail: string,
    oldRole: string,
    newRole: string
  ): Promise<void> {
    await this.createAlert(
      'SUSPICIOUS_ACTIVITY',
      `Role change: ${targetUserEmail} from ${oldRole} to ${newRole}`,
      'HIGH',
      userId,
      userEmail,
      { targetUserId, targetUserEmail, oldRole, newRole }
    );

    await AuditLogger.logRoleChange(userId, userEmail, targetUserId, targetUserEmail, oldRole, newRole);
  }

  /**
   * Monitor unauthorized access attempts
   */
  static async monitorUnauthorizedAccess(
    userId: string,
    userEmail: string,
    resource: string,
    action: string
  ): Promise<void> {
    await this.createAlert(
      'UNAUTHORIZED_ACCESS',
      `Unauthorized access attempt: ${userEmail} tried to ${action} ${resource}`,
      'CRITICAL',
      userId,
      userEmail,
      { resource, action }
    );

    await AuditLogger.logSecurityViolation(
      userId,
      userEmail,
      `Unauthorized access to ${resource}`,
      { action }
    );
  }

  /**
   * Monitor session timeout
   */
  static async monitorSessionTimeout(
    userId: string,
    userEmail: string
  ): Promise<void> {
    await this.createAlert(
      'SUSPICIOUS_ACTIVITY',
      `Session timeout for ${userEmail}`,
      'LOW',
      userId,
      userEmail
    );

    await AuditLogger.logSessionTimeout(userId, userEmail);
  }

  /**
   * Create security alert
   */
  private static async createAlert(
    type: SecurityAlert['type'],
    message: string,
    severity: SecurityAlert['severity'],
    userId?: string,
    userEmail?: string,
    details?: any
  ): Promise<void> {
    const alert: SecurityAlert = {
      id: Date.now().toString(),
      type,
      message,
      severity,
      timestamp: new Date(),
      userId,
      userEmail,
      details
    };

    this.alerts.push(alert);
    this.saveToStorage(); // Save to localStorage

    // Store alert in Firestore for centralized monitoring
    try {
      await addDoc(collection(db, this.SECURITY_ALERTS_COLLECTION), {
        type,
        message,
        severity,
        timestamp: serverTimestamp(),
        userId: userId || null,
        userEmail: userEmail || null,
        details: details ? JSON.stringify(details) : null
      });
      console.log(`🚨 Security alert stored in Firestore: ${message}`);
    } catch (error) {
      console.error('Failed to store security alert in Firestore:', error);
    }

    // Log the alert
    console.warn(`🚨 SECURITY ALERT [${severity}]: ${message}`, details);

    // Clean up old alerts
    this.cleanupOldAlerts();
  }

  /**
   * Get recent security alerts (from localStorage and Firestore)
   */
  static async getRecentAlerts(limitCount: number = 50): Promise<SecurityAlert[]> {
    // Load alerts from Firestore
    let firestoreAlerts: SecurityAlert[] = [];
    try {
      const q = query(
        collection(db, this.SECURITY_ALERTS_COLLECTION),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      
      firestoreAlerts = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data.type,
          message: data.message,
          severity: data.severity,
          timestamp: data.timestamp?.toDate?.() || new Date(),
          userId: data.userId,
          userEmail: data.userEmail,
          details: data.details ? JSON.parse(data.details) : undefined
        };
      });
      
      console.log('🚨 Loaded alerts from Firestore:', firestoreAlerts.length);
    } catch (error) {
      console.error('Failed to load alerts from Firestore:', error);
    }

    // Combine localStorage and Firestore alerts
    const allAlerts = [...this.alerts, ...firestoreAlerts];
    
    return allAlerts
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limitCount);
  }

  /**
   * Get alerts by severity
   */
  static getAlertsBySeverity(severity: SecurityAlert['severity']): SecurityAlert[] {
    return this.alerts.filter(alert => alert.severity === severity);
  }

  /**
   * Get alerts by user
   */
  static getAlertsByUser(userEmail: string): SecurityAlert[] {
    return this.alerts.filter(alert => alert.userEmail === userEmail);
  }

  /**
   * Get security statistics
   */
  static async getSecurityStats(): Promise<{
    totalAlerts: number;
    criticalAlerts: number;
    highAlerts: number;
    mediumAlerts: number;
    lowAlerts: number;
    recentLoginFailures: number;
    recentLoginSuccesses: number;
    failureDetails: Array<{
      id: string;
      email: string;
      ipAddress: string;
      timestamp: Date;
      userAgent: string;
    }>;
    successDetails: Array<{
      id: string;
      email: string;
      ipAddress: string;
      timestamp: Date;
      userAgent: string;
      userId: string;
    }>;
  }> {
    const now = new Date();
    const recentThreshold = new Date(now.getTime() - this.LOGIN_WINDOW_MS);

    console.log('🔒 getSecurityStats called');
    console.log('🔒 loginAttempts Map size:', this.loginAttempts.size);
    console.log('🔒 loginAttempts entries:', Array.from(this.loginAttempts.entries()));
    console.log('🔒 recentThreshold:', recentThreshold);
    console.log('🔒 now:', now);

    // Load recent login failures from Firestore
    let firestoreFailures = 0;
    let firestoreFailureDetails: any[] = [];
    try {
      console.log('🔒 Querying Firestore for login failures...');
      console.log('🔒 Query threshold:', recentThreshold);
      
      const q = query(
        collection(db, this.LOGIN_ATTEMPTS_COLLECTION),
        where('success', '==', false),
        where('timestamp', '>=', recentThreshold),
        orderBy('timestamp', 'desc')
      );
      
      console.log('🔒 Executing Firestore query...');
      const querySnapshot = await getDocs(q);
      firestoreFailures = querySnapshot.size;
      
      console.log('🔒 Firestore login failures (last 24h):', firestoreFailures);
      console.log('🔒 Firestore query returned', querySnapshot.docs.length, 'documents');
      
      // Collect the actual failure details
      querySnapshot.forEach(doc => {
        const data = doc.data();
        firestoreFailureDetails.push({
          id: doc.id,
          email: data.userEmail,
          ipAddress: data.ipAddress || 'unknown',
          timestamp: data.timestamp?.toDate?.() || new Date(),
          userAgent: data.userAgent || 'unknown'
        });
        console.log('🔒 Firestore failure doc:', doc.id, data);
      });
    } catch (error) {
      console.error('🔒 Failed to load login failures from Firestore:', error);
      console.error('🔒 Error details:', error);
    }

    // Load recent successful logins from Firestore
    let firestoreSuccesses = 0;
    let firestoreSuccessDetails: any[] = [];
    try {
      console.log('🔒 Querying Firestore for successful logins...');
      
      const q = query(
        collection(db, this.LOGIN_ATTEMPTS_COLLECTION),
        where('success', '==', true),
        where('timestamp', '>=', recentThreshold),
        orderBy('timestamp', 'desc'),
        limit(20) // Limit to last 20 successful logins
      );
      
      const querySnapshot = await getDocs(q);
      firestoreSuccesses = querySnapshot.size;
      
      console.log('🔒 Firestore successful logins (last 24h):', firestoreSuccesses);
      
      // Collect the actual success details
      querySnapshot.forEach(doc => {
        const data = doc.data();
        firestoreSuccessDetails.push({
          id: doc.id,
          email: data.userEmail,
          ipAddress: data.ipAddress || 'unknown',
          timestamp: data.timestamp?.toDate?.() || new Date(),
          userAgent: data.userAgent || 'unknown',
          userId: data.userId || 'unknown'
        });
      });
    } catch (error) {
      console.error('🔒 Failed to load successful logins from Firestore:', error);
    }

    // For debugging, let's show all attempts and their dates
    const allAttempts = Array.from(this.loginAttempts.entries());
    console.log('🔒 All login attempts with dates:');
    allAttempts.forEach(([email, attempt]) => {
      const attemptDate = new Date(attempt.lastAttempt);
      const isRecent = attemptDate > recentThreshold;
      console.log(`🔒 ${email}: ${attemptDate} (count: ${attempt.count}) - Recent: ${isRecent}`);
    });

    const recentFailures = Array.from(this.loginAttempts.values())
      .filter(attempt => {
        const attemptDate = new Date(attempt.lastAttempt);
        const isRecent = attemptDate > recentThreshold;
        return isRecent;
      })
      .reduce((sum, attempt) => sum + attempt.count, 0);

    console.log('🔒 recentLoginFailures calculated:', recentFailures);
    console.log('🔒 Total failures (local + Firestore):', recentFailures + firestoreFailures);

    // Load all alerts from Firestore for accurate counts
    const allAlerts = await this.getRecentAlerts(1000); // Get all recent alerts
    
    const stats = {
      totalAlerts: allAlerts.length,
      criticalAlerts: allAlerts.filter(a => a.severity === 'CRITICAL').length,
      highAlerts: allAlerts.filter(a => a.severity === 'HIGH').length,
      mediumAlerts: allAlerts.filter(a => a.severity === 'MEDIUM').length,
      lowAlerts: allAlerts.filter(a => a.severity === 'LOW').length,
      recentLoginFailures: recentFailures + firestoreFailures, // Combine local and Firestore failures
      recentLoginSuccesses: firestoreSuccesses, // Successful logins count
      failureDetails: firestoreFailureDetails, // Include detailed failure information
      successDetails: firestoreSuccessDetails // Include detailed success information
    };

    console.log('🔒 Final stats:', stats);
    return stats;
  }

  /**
   * Get client IP address (simplified)
   */
  private static async getClientIP(): Promise<string> {
    try {
      // Try to get IP from a public service
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'unknown';
    } catch (error) {
      console.error('Failed to get client IP:', error);
      return 'unknown';
    }
  }

  /**
   * Clean up old alerts
   */
  private static cleanupOldAlerts(): void {
    const cutoff = new Date(Date.now() - this.ALERT_RETENTION_MS);
    this.alerts = this.alerts.filter(alert => alert.timestamp > cutoff);
  }

  /**
   * Clear all alerts (for testing)
   */
  static clearAlerts(): void {
    this.alerts = [];
    this.loginAttempts.clear();
  }
}
